import React from 'react';
import { Users } from 'lucide-react';
import { iUser } from '../../../components/type';
import { useTranslation } from 'react-i18next';
import { ActiveUser, getUserColor, getUserInitials } from '../../hooks/useCollaboration';
import { UserAvatar } from '../Layout/ActiveUsersBar';

interface Collaborator {
    user: iUser;
    edit: boolean;
}

interface CollaboratorModalProps {
    collaborators: Collaborator[];
    onToggleAccess: (userId: string) => void;
    onGrantAllAccess: () => void;
    activeUsers?: ActiveUser[];
}

export function CollaboratorModal({
    collaborators,
    onToggleAccess,
    onGrantAllAccess,
    activeUsers = [],
}: CollaboratorModalProps) {

    const { t } = useTranslation();

    const isOnline = (userId: string) =>
        activeUsers.some(u => u.user_id === userId);

    const getActiveUser = (userId: string): ActiveUser | null =>
        activeUsers.find(u => u.user_id === userId) ?? null;

    const buildFakeActiveUser = (user: iUser): ActiveUser => ({
        user_id: user._id ?? '',
        fullname: user.fullname ?? '',
        email: user.email ?? '',
        color: getUserColor(user.fullname || user.email || user._id || ''),
        initials: getUserInitials(user.fullname ?? ''),
    });

    return (
        <div className="modal fade" id="collaboratorModalEdit" tabIndex={-1} aria-labelledby="collaboratorModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title d-flex align-items-center gap-2" id="collaboratorModalLabel">
                            <Users size={24} />
                            {t('features.swimlane.enableMapEdit')}
                        </h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <div className="modal-body">
                        {/* Barre "En ligne" si des participants actifs */}
                        {activeUsers.length > 0 && (
                            <div className="d-flex align-items-center gap-2 mb-3 p-2 rounded"
                                style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                                <div className="d-flex" style={{ gap: '-4px' }}>
                                    {activeUsers.slice(0, 5).map(u => (
                                        <div key={u.user_id} style={{ marginRight: '-6px' }}>
                                            <UserAvatar user={u} size={26} />
                                        </div>
                                    ))}
                                </div>
                                <span className="text-sm text-green-700 fw-medium ms-2">
                                    {activeUsers.length} {t('features.swimlane.participantsOnline') || 'participant(s) en ligne'}
                                </span>
                            </div>
                        )}

                        <div className="overflow-auto" style={{ maxHeight: "400px" }}>
                            {collaborators.map((collaborator) => {
                                const online = isOnline(collaborator.user._id ?? '');
                                const activeUser = getActiveUser(collaborator.user._id ?? '') ?? buildFakeActiveUser(collaborator.user);

                                return (
                                    <div
                                        key={collaborator.user._id}
                                        className="d-flex justify-content-between align-items-center p-3 bg-light rounded mb-2"
                                        style={{
                                            borderLeft: `4px solid ${activeUser.color}`,
                                        }}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            {/* Avatar coloré */}
                                            <div className="position-relative">
                                                <UserAvatar user={activeUser} size={36} />
                                                {/* Point de statut en ligne */}
                                                <div
                                                    className="position-absolute rounded-circle"
                                                    style={{
                                                        width: 10,
                                                        height: 10,
                                                        backgroundColor: online ? '#22c55e' : '#9ca3af',
                                                        border: '2px solid white',
                                                        bottom: 0,
                                                        right: 0,
                                                    }}
                                                    title={online
                                                        ? (t('features.swimlane.online') || 'En ligne')
                                                        : (t('features.swimlane.offline') || 'Hors ligne')}
                                                />
                                            </div>

                                            <div>
                                                <h6 className="mb-0 d-flex align-items-center gap-1">
                                                    {collaborator.user.fullname}
                                                    {online && (
                                                        <span
                                                            className="badge rounded-pill text-white"
                                                            style={{
                                                                fontSize: 10,
                                                                backgroundColor: activeUser.color,
                                                                padding: '2px 7px',
                                                            }}
                                                        >
                                                            {t('features.swimlane.online') || 'En ligne'}
                                                        </span>
                                                    )}
                                                </h6>
                                                <small className="text-muted">{collaborator.user.email}</small>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                            <span className={`badge ${collaborator.edit ? 'bg-success' : 'bg-secondary'}`}>
                                                {collaborator.edit
                                                    ? t('features.swimlane.accessGranted')
                                                    : t('features.swimlane.noAccess')}
                                            </span>
                                            <button
                                                onClick={() => onToggleAccess(collaborator.user._id ?? "")}
                                                className={`btn btn-sm ${collaborator.edit ? 'btn-outline-danger' : 'btn-outline-success'}`}
                                            >
                                                {collaborator.edit
                                                    ? t('features.swimlane.removeAccess')
                                                    : t('features.swimlane.giveAccess')}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                            {t('words.close')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
